import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, IconButton, Typography, Button, Box } from '@mui/material';
import { AccessTime, CloseOutlined, Settings, DeleteOutlined } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import {
  CalendarTaskFrom,
  CalendarRoutineFrom,
  EnergyFeedbackType,
  type MealFoodAmount,
} from '@demind-inc/core';

import './AISuggestedEventModal.scss';
import { LoadingButton } from '@mui/lab';
import {
  SuggestedEventType,
  circadianPhaseInfo,
  settingsSidebarStateAtom,
  translateMinToHHhMMmString,
  useCalendarContext,
  useCircadianContext,
  useTodoTasksCache,
} from '../../../data-access';
import { CalendarAccountDropdown } from '../EventMenu/CalendarAccountDropdown';
import { extractEmojiAndTitle, getTitleBgColor } from './helpers';
import { ReactComponent as SparkleIcon } from '../../../assets/scheduler/sparkle.svg';
import { useGeneralSettings } from '../../../hooks';
import { BasicDialog, TaskCheckBox } from '../../common';
import TodoSuggestedEventSheetContent from './TodoSuggestedEventSheetContent';
import RoutineSuggestedEventSheetContent from './RoutineSuggestedEventSheetContent';
import { TimeSelectorItem, TimeSelectorRangeSeparator } from './TimeSelectorItem';
import { EditableTitle } from './EditableTitle';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { Path } from '../../../routing';
import { EventEnergyFeedback } from '../EventMenu/EventEnergyFeedback';
import { getDueDateYYYYMMDD } from '../../../helpers';
import clsx from 'clsx';
import RichContextSummary from './RichContextSummary';
import type { UnifiedChatRichContext } from '../../../data-access';
import MealContentEditor, {
  type MealContentDraft,
} from '../../AiAssistantChat/RichContext/MealContentEditor';
import {
  getMealSummaryPayloadFromSignals,
  type AnyRecord,
} from '../../AiAssistantChat/RichContext/mealSignalUtils';
import { normalizeRichContentType } from '../../AiAssistantChat/RichContext/contentCardUtils';
import { useTranslation } from 'react-i18next';
import { getCircadianPhaseTitle } from '../../DataView/i18n';

interface AISuggestedEventModalProps {
  sessionType: SuggestedEventType;
  name: string;
  startTime: string; // ISO string
  endTime: string; // ISO string
  description: string;
  forceEnableSaveCta?: boolean;
  canUpdate?: boolean;
  isSaving?: boolean;
  isPredicted?: boolean;
  isEditedManually?: boolean;
  canAcceptCalendar?: boolean;
  actualDataComp?: React.ReactNode;
  hasNoSleepData?: boolean;
  canReject?: boolean;
  canDelete?: boolean;
  considerAcrossDays?: boolean;
  visible: boolean;
  taskFrom?: CalendarTaskFrom;
  routineFrom?: CalendarRoutineFrom;
  calendarId?: string;
  energyFeedback?: EnergyFeedbackType;
  onAskAI: () => void;
  onUpdate?: (startTime: string, endTime: string, title?: string) => void;
  onAcceptCalendar?: (calendarId: string, startTime: string, endTime: string) => void;
  onClose: () => void;
  onReject?: () => void;
  onDelete?: () => void | Promise<boolean | void>;
  onSaveEnergyFeedback?: (feedback: EnergyFeedbackType) => Promise<void>;
  disableInteraction?: boolean;
  richContext?: UnifiedChatRichContext | null;
  onSaveMealContent?: (content: { title: string; foodItems: MealFoodAmount[] }) => Promise<void>;
  onToggleTaskComplete?: (completed: boolean, mutateDate?: string) => void;
  onToggleRoutineComplete?: (completed: boolean) => void;
  isTaskStatusUpdating?: boolean;
  routineCompleted?: boolean;
  isRoutineUpdating?: boolean;
}

const toMealFoodItems = (foodItemsRaw: unknown): MealFoodAmount[] => {
  if (!Array.isArray(foodItemsRaw)) return [];
  return foodItemsRaw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const rawItem = item as Record<string, unknown>;
      const name = typeof rawItem.name === 'string' ? rawItem.name.trim() : '';
      const amount =
        typeof rawItem.amount === 'number'
          ? rawItem.amount
          : typeof rawItem.amount === 'string'
            ? Number.parseFloat(rawItem.amount)
            : NaN;
      if (!name || !Number.isFinite(amount) || amount <= 0) return null;
      return { name, amount };
    })
    .filter(Boolean) as MealFoodAmount[];
};

const getMealDraftFromPayload = (payload: AnyRecord | null): MealContentDraft | null => {
  if (!payload) return null;

  const nestedMealContent =
    payload.meal_content &&
    typeof payload.meal_content === 'object' &&
    !Array.isArray(payload.meal_content)
      ? (payload.meal_content as AnyRecord)
      : null;
  if (nestedMealContent) return getMealDraftFromPayload(nestedMealContent);

  const mealDetails = Array.isArray(payload.meal_details)
    ? (payload.meal_details.filter(
        (detail) => detail && typeof detail === 'object' && !Array.isArray(detail)
      ) as AnyRecord[])
    : [];
  const target = mealDetails[0] ?? payload;

  const title = typeof target.title === 'string' ? target.title.trim() : '';
  const foodItems = toMealFoodItems(target.food_items ?? target.foodItems);
  if (!title && foodItems.length === 0) return null;

  return { title, foodItems };
};

const getMealDraftFromRichContext = (
  context: UnifiedChatRichContext | null | undefined
): MealContentDraft | null => {
  if (!context) return null;
  const contentList = context.content
    ? Array.isArray(context.content)
      ? context.content
      : [context.content]
    : [];

  for (const content of contentList) {
    if (!content || typeof content !== 'object' || Array.isArray(content)) continue;
    const contentRecord = content as AnyRecord;
    const type = normalizeRichContentType(
      typeof contentRecord.type === 'string' ? contentRecord.type : undefined
    );
    const payload =
      contentRecord.payload &&
      typeof contentRecord.payload === 'object' &&
      !Array.isArray(contentRecord.payload)
        ? (contentRecord.payload as AnyRecord)
        : null;
    if (!payload) continue;
    if (type === 'meal') {
      const draft = getMealDraftFromPayload(payload);
      if (draft) return draft;
    }
  }

  return getMealDraftFromPayload(getMealSummaryPayloadFromSignals(context));
};

const AISuggestedEventModal: React.FC<AISuggestedEventModalProps> = memo(
  ({
    sessionType,
    name,
    startTime,
    endTime,
    description,
    forceEnableSaveCta = false,
    canUpdate = false,
    isSaving = false,
    isPredicted = false,
    isEditedManually = false,
    canAcceptCalendar = false,
    actualDataComp,
    hasNoSleepData = false,
    canReject = false,
    canDelete = false,
    visible,
    taskFrom,
    routineFrom,
    calendarId,
    energyFeedback,
    onAskAI,
    onUpdate = () => void 0,
    onAcceptCalendar = () => void 0,
    onClose,
    onReject = () => void 0,
    onDelete = () => void 0,
    onSaveEnergyFeedback,
    considerAcrossDays = false,
    disableInteraction = false,
    richContext = null,
    onSaveMealContent,
    onToggleTaskComplete,
    onToggleRoutineComplete,
    isTaskStatusUpdating,
    routineCompleted = false,
    isRoutineUpdating = false,
  }) => {
    const { t } = useTranslation();
    const {
      generalSettings: { timeFormat },
    } = useGeneralSettings();
    const { findPhaseForTaskTime } = useCircadianContext();
    const shouldShowTime = sessionType === 'sleep' || sessionType === 'meal';
    const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
    const navigate = useNavigate();
    const { mainCalendar, calendarAccountsToCreateEvents, findCalendarItem } = useCalendarContext();
    const { findInCacheByDate } = useTodoTasksCache();
    const [selectedCalendarId, setSelectedCalendarId] = useState('');
    const [editableStartTime, setEditableStartTime] = useState<string>(startTime);
    const [editableEndTime, setEditableEndTime] = useState<string>(endTime);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedEnergyFeedback, setSelectedEnergyFeedback] = useState<EnergyFeedbackType>();
    const [editableTitle, setEditableTitle] = useState<string>(name);
    const [localRichContext, setLocalRichContext] = useState<UnifiedChatRichContext | null>(
      richContext ?? null
    );
    const [mealContentEditorOpen, setMealContentEditorOpen] = useState(false);
    const [isSavingMealContent, setIsSavingMealContent] = useState(false);
    const isFutureDate = dayjs(startTime).isAfter(dayjs());
    const mealDraftFromContext = useMemo(
      () => getMealDraftFromRichContext(localRichContext ?? richContext),
      [localRichContext, richContext]
    );
    const [localMealDraft, setLocalMealDraft] = useState<MealContentDraft | null>(
      mealDraftFromContext
    );

    // Get task completion status for tasks
    const linkedTask = useMemo(() => {
      if (!taskFrom?.taskId || !taskFrom?.boardId || !startTime) {
        return undefined;
      }
      const tasks = findInCacheByDate(dayjs(startTime).format('YYYY-MM-DD'), taskFrom.boardId);
      return tasks.find((task) => task.taskId === taskFrom.taskId);
    }, [taskFrom?.taskId, taskFrom?.boardId, startTime, findInCacheByDate]);

    const initialTaskCompleted = linkedTask?.completed ?? false;
    const [localTaskCompleted, setLocalTaskCompleted] = useState(initialTaskCompleted);
    const [localRoutineCompleted, setLocalRoutineCompleted] = useState(routineCompleted);

    useEffect(() => {
      if (mainCalendar?.calendarId) {
        setSelectedCalendarId(mainCalendar.calendarId);
      }
    }, [mainCalendar]);

    useEffect(() => {
      setEditableStartTime(startTime);
      setEditableEndTime(endTime);
    }, [startTime, endTime]);

    useEffect(() => {
      setEditableTitle(name);
    }, [name]);

    useEffect(() => {
      setSelectedEnergyFeedback(energyFeedback);
    }, [energyFeedback]);

    useEffect(() => {
      setLocalRichContext(richContext ?? null);
    }, [richContext]);

    useEffect(() => {
      setLocalMealDraft(mealDraftFromContext);
    }, [mealDraftFromContext]);

    // Sync local task completion state with linkedTask
    useEffect(() => {
      if (linkedTask !== undefined) {
        setLocalTaskCompleted(linkedTask.completed ?? false);
      }
    }, [linkedTask]);

    // Sync local routine completion state with prop
    useEffect(() => {
      setLocalRoutineCompleted(routineCompleted);
    }, [routineCompleted]);

    const canSaveCta = useMemo(() => {
      if (canUpdate) {
        const hasTimeChanged =
          !dayjs(editableStartTime).isSame(dayjs(startTime)) ||
          !dayjs(editableEndTime).isSame(dayjs(endTime));
        const titleChanged = sessionType === 'task' && editableTitle !== name;
        const hasChanges = hasTimeChanged || titleChanged;

        return (
          !isSaving &&
          dayjs(editableEndTime).isAfter(dayjs(editableStartTime)) &&
          (forceEnableSaveCta || hasChanges)
        );
      }

      if (canAcceptCalendar) {
        return !isSaving && !!selectedCalendarId;
      }

      return false;
    }, [
      canUpdate,
      canAcceptCalendar,
      isSaving,
      selectedCalendarId,
      editableStartTime,
      editableEndTime,
      startTime,
      endTime,
      forceEnableSaveCta,
      sessionType,
      editableTitle,
      name,
    ]);

    const { durationLabel, phase, phaseKey } = useMemo(() => {
      const startDate = dayjs(editableStartTime);
      let endDate = dayjs(editableEndTime);

      if (!startDate.isValid() || !endDate.isValid()) {
        return { durationLabel: null, phase: undefined, phaseKey: undefined };
      }

      if (!endDate.isAfter(startDate)) {
        if (considerAcrossDays) {
          endDate = endDate.add(1, 'day');
        } else {
          return { durationLabel: null, phase: undefined, phaseKey: undefined };
        }
      }

      const durationMin = endDate.diff(startDate, 'minute');
      if (durationMin <= 0) {
        return { durationLabel: null, phase: undefined, phaseKey: undefined };
      }

      const targetPhase = findPhaseForTaskTime(
        startDate.format('YYYY-MM-DD HH:mm'),
        endDate.format('YYYY-MM-DD HH:mm')
      );

      return {
        durationLabel: translateMinToHHhMMmString(durationMin),
        phase: targetPhase ? circadianPhaseInfo[targetPhase] : undefined,
        phaseKey: targetPhase,
      };
    }, [considerAcrossDays, editableEndTime, editableStartTime, findPhaseForTaskTime]);

    const discardAction = useMemo<'discard' | 'delete' | ''>(() => {
      if (canReject) {
        return 'discard';
      }
      if (canDelete) {
        if (isPredicted) {
          return 'discard';
        }
        return 'delete';
      }
      return '';
    }, [canReject, canDelete, isPredicted]);
    const discardText = discardAction ? t(`calendar.common.${discardAction}`) : '';
    const discardActionInSentence = discardAction ? t(`calendar.common.${discardAction}Lower`) : '';

    const shouldUseScopedRoutineDelete = useMemo(
      () => canDelete && sessionType === 'routine' && !isPredicted,
      [canDelete, isPredicted, sessionType]
    );

    const runDeleteSafely = useCallback(() => {
      void Promise.resolve(onDelete()).catch(() => {
        // Deletion handlers are responsible for user-facing error feedback.
      });
    }, [onDelete]);

    const handleDiscard = () => {
      setTimeout(() => {
        if (canReject) {
          onReject();
        } else if (canDelete) {
          runDeleteSafely();
        }
      }, 100);
    };

    const handleStartTimeChange = (date: Dayjs | null) => {
      if (!date) {
        return;
      }

      if (considerAcrossDays) {
        let adjustedStartTime = dayjs(editableEndTime)
          .set('hour', date.hour())
          .set('minute', date.minute())
          .set('second', 0);
        if (adjustedStartTime.isAfter(dayjs(editableEndTime))) {
          adjustedStartTime = adjustedStartTime.subtract(1, 'day');
        }
        setEditableStartTime(adjustedStartTime.toISOString());
      } else {
        setEditableStartTime((prev) =>
          dayjs(prev)
            .set('hour', date.hour())
            .set('minute', date.minute())
            .set('second', 0)
            .toISOString()
        );
      }
    };

    const handleEndTimeChange = (date: Dayjs | null) => {
      if (date) {
        setEditableEndTime((prev) =>
          dayjs(prev)
            .set('hour', date.hour())
            .set('minute', date.minute())
            .set('second', 0)
            .toISOString()
        );
      }
    };

    const hasMealDetailsInRichContext = useMemo(
      () =>
        !!localMealDraft &&
        (localMealDraft.foodItems.length > 0 || Boolean(localMealDraft.title.trim())),
      [localMealDraft]
    );
    const shouldShowMealEditorInRichContext = useMemo(
      () =>
        sessionType === 'meal' &&
        mealContentEditorOpen &&
        hasMealDetailsInRichContext &&
        !!onSaveMealContent,
      [sessionType, mealContentEditorOpen, hasMealDetailsInRichContext, onSaveMealContent]
    );

    const handleSaveMealContent = useCallback(
      async (draft: MealContentDraft) => {
        if (!onSaveMealContent) return;
        setIsSavingMealContent(true);
        try {
          await onSaveMealContent(draft);
          setLocalMealDraft(draft);
          setMealContentEditorOpen(false);
        } finally {
          setIsSavingMealContent(false);
        }
      },
      [onSaveMealContent]
    );

    const handleOpenMealSettings = useCallback(() => {
      setSettingsSidebarState({
        selectedParentKey: 'aiAssistant',
        expandedParentKeys: [],
      });
      navigate(`${Path.HOME}/settings`);
      onClose();
    }, [onClose, setSettingsSidebarState, navigate]);

    const canSelectEnergyFeedback = useMemo(
      () =>
        !isPredicted && dayjs(editableEndTime).isSameOrBefore(dayjs()) && sessionType !== 'sleep',
      [editableEndTime, isPredicted, sessionType]
    );

    const calendarDisplay = useMemo(() => {
      if (!calendarId && !mainCalendar) {
        return undefined;
      }

      const targetCalendar = calendarId ? findCalendarItem(calendarId) : mainCalendar;

      if (!targetCalendar) {
        return undefined;
      }

      return {
        color: targetCalendar.color,
        label:
          targetCalendar.name ||
          targetCalendar.internalGCalendarId ||
          targetCalendar.rootEmail ||
          '',
      };
    }, [calendarId, findCalendarItem, mainCalendar]);

    const handleEnergyFeedbackChange = useCallback(
      async (feedback: EnergyFeedbackType) => {
        if (!canSelectEnergyFeedback || !onSaveEnergyFeedback) {
          return;
        }
        if (feedback === selectedEnergyFeedback) {
          return;
        }

        const prevFeedback = selectedEnergyFeedback;
        setSelectedEnergyFeedback(feedback);
        onSaveEnergyFeedback(feedback);
      },
      [canSelectEnergyFeedback, selectedEnergyFeedback, onSaveEnergyFeedback]
    );

    // Extract emoji and clean title for display
    const { emoji, title: cleanTitle } = extractEmojiAndTitle(name, sessionType);
    const isTaskType = sessionType === 'task';
    const sessionTypeLabel = t(`calendar.suggested.sessionTypes.${sessionType}`, {
      defaultValue: sessionType,
    });
    const showV3RichContextSummary =
      (sessionType === 'sleep' || sessionType === 'meal' || sessionType === 'workout') &&
      !!(localRichContext ?? richContext);
    const showMealPreviewSection = useMemo(() => {
      if (sessionType !== 'meal') {
        return false;
      }

      const showAddMealContent =
        !!onSaveMealContent && !disableInteraction && !hasMealDetailsInRichContext;

      return (
        showAddMealContent ||
        (showV3RichContextSummary && !shouldShowMealEditorInRichContext) ||
        (showV3RichContextSummary && shouldShowMealEditorInRichContext)
      );
    }, [
      sessionType,
      onSaveMealContent,
      disableInteraction,
      hasMealDetailsInRichContext,
      showV3RichContextSummary,
      shouldShowMealEditorInRichContext,
    ]);
    const isRoutineType = sessionType === 'routine';

    // Handle task completion toggle
    const handleTaskToggleComplete = useCallback(
      (checked: boolean) => {
        if (disableInteraction || isTaskStatusUpdating || !linkedTask || !linkedTask.boardId) {
          return;
        }
        // Optimistic update
        setLocalTaskCompleted(checked);
        const mutateDate =
          getDueDateYYYYMMDD(linkedTask.dueDateTime) || dayjs().format('YYYY-MM-DD');
        onToggleTaskComplete?.(checked, mutateDate);
      },
      [disableInteraction, isTaskStatusUpdating, linkedTask, onToggleTaskComplete]
    );

    // Handle routine completion toggle
    const handleRoutineToggleComplete = useCallback(
      (checked: boolean) => {
        if (disableInteraction || isRoutineUpdating) {
          return;
        }
        // Optimistic update
        setLocalRoutineCompleted(checked);
        onToggleRoutineComplete?.(checked);
      },
      [disableInteraction, isRoutineUpdating, onToggleRoutineComplete]
    );

    return (
      <>
        <Dialog
          open={visible}
          onClose={onClose}
          disableRestoreFocus
          className="ai-suggested-event-modal"
          PaperProps={{
            className: 'ai-suggested-event-modal__paper',
          }}
        >
          <DialogContent className="ai-suggested-event-modal__content">
            <div className="ai-suggested-event-modal__container">
              {/* Header */}
              <div className="ai-suggested-event-modal__header">
                <div className="ai-suggested-event-modal__ai-badge">
                  <SparkleIcon width={11} height={11} />
                  <span>Lifestack AI</span>
                </div>
                <div className="ai-suggested-event-modal__header-actions">
                  {sessionType === 'meal' && (
                    <IconButton
                      className="ai-suggested-event-modal__suggested-settings-btn"
                      onClick={handleOpenMealSettings}
                      size="small"
                      aria-label={t('calendar.suggested.openMealSettings')}
                    >
                      <Settings fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    className="ai-suggested-event-modal__back-button"
                    onClick={onClose}
                    size="small"
                    aria-label={t('calendar.common.close')}
                  >
                    <CloseOutlined />
                  </IconButton>
                </div>
              </div>
              <div className="ai-suggested-event-modal__title-section">
                <div className="ai-suggested-event-modal__title-wrapper">
                  <div
                    className="ai-suggested-event-modal__session-icon"
                    style={{ backgroundColor: getTitleBgColor(sessionType) }}
                  >
                    <Typography className="ai-suggested-event-modal__session-emoji">
                      {emoji}
                    </Typography>
                  </div>

                  {isTaskType ? (
                    <div className="ai-suggested-event-modal__title-with-checkbox">
                      <EditableTitle
                        name={name}
                        onUpdateName={setEditableTitle}
                        disableInteraction={disableInteraction}
                      />
                    </div>
                  ) : (
                    <div className="ai-suggested-event-modal__title-block">
                      <Typography className="ai-suggested-event-modal__title">
                        {cleanTitle}
                      </Typography>
                    </div>
                  )}
                </div>
                {(durationLabel || phase?.label) && (
                  <div className="ai-suggested-event-modal__phase-container">
                    {durationLabel && (
                      <div className="ai-suggested-event-modal__phase-container__time">
                        <AccessTime className="ai-suggested-event-modal__phase-container__time__icon" />
                        {durationLabel}
                      </div>
                    )}
                    {phase?.label && (
                      <div
                        className="ai-suggested-event-modal__phase-container__phase"
                        style={{ backgroundColor: `${phase.color}1f`, color: phase.color }}
                      >
                        <span
                          className="ai-suggested-event-modal__phase-container__phase__dot"
                          style={{ backgroundColor: phase.color }}
                        />
                        {getCircadianPhaseTitle(t, phaseKey ?? 'default', phase.label)}
                      </div>
                    )}
                  </div>
                )}
                <div className="ai-suggested-event-modal__time-container">
                  <TimeSelectorItem
                    label={t('calendar.common.starts')}
                    value={dayjs(editableStartTime)}
                    onChange={handleStartTimeChange}
                    className="ai-suggested-event-modal__time-item"
                    disabled={disableInteraction}
                  />
                  <TimeSelectorRangeSeparator />
                  <TimeSelectorItem
                    label={t('calendar.common.ends')}
                    value={dayjs(editableEndTime)}
                    onChange={handleEndTimeChange}
                    className="ai-suggested-event-modal__time-item"
                    disabled={disableInteraction}
                  />
                </div>
              </div>

              <div className="ai-suggested-event-modal__scrollable-wrapper">
                {/* Use RoutineSuggestedEventSheetContent for routine events */}
                {sessionType === 'routine' && routineFrom ? (
                  <RoutineSuggestedEventSheetContent
                    routineFrom={routineFrom}
                    onClose={onClose}
                    onDeleteRoutine={onDelete}
                    disableInteraction={disableInteraction}
                  />
                ) : sessionType === 'task' && taskFrom ? (
                  <TodoSuggestedEventSheetContent
                    startTime={editableStartTime}
                    onClose={onClose}
                    isPredicted={isPredicted}
                    description={description}
                    taskFrom={taskFrom}
                    disableInteraction={disableInteraction}
                    onToggleTaskComplete={handleTaskToggleComplete}
                  />
                ) : (
                  <></>
                )}
                {!['task', 'routine'].includes(sessionType) && (
                  <>
                    {!isFutureDate && hasNoSleepData && sessionType === 'sleep' && (
                      <Box className="ai-suggested-event-modal__no-sleep-data">
                        <Typography variant="subtitle2">
                          {t('calendar.suggested.noSleepData')}
                        </Typography>
                      </Box>
                    )}

                    {/* Why this time section */}

                    {!!description && !actualDataComp && !showV3RichContextSummary && (
                      <div className="ai-suggested-event-modal__section">
                        <Typography className="ai-suggested-event-modal__why-title">
                          ℹ️{' '}
                          {isEditedManually
                            ? t('calendar.suggested.originalSuggestions')
                            : t('calendar.suggested.whySessionTime', {
                                sessionType: sessionTypeLabel,
                              })}
                        </Typography>
                        <div className="ai-suggested-event-modal__description-container">
                          <div
                            className="ai-suggested-event-modal__description-content"
                            dangerouslySetInnerHTML={{ __html: description }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Actual data section */}
                {!isPredicted && actualDataComp && !showV3RichContextSummary && (
                  <div className="ai-suggested-event-modal__actual-data-section">
                    <Typography className="ai-suggested-event-modal__actual-data-title">
                      {t('calendar.suggested.yourSessionData', { sessionType: sessionTypeLabel })}
                    </Typography>
                    {actualDataComp}
                  </div>
                )}
                {showMealPreviewSection && (
                  <div className="ai-suggested-event-modal__section ai-suggested-event-modal__meal-preview-section">
                    <Typography className="ai-suggested-event-modal__section-title">
                      {t('calendar.suggested.mealPreview')}
                    </Typography>
                    {!!onSaveMealContent &&
                      !disableInteraction &&
                      !hasMealDetailsInRichContext &&
                      (!mealContentEditorOpen ? (
                        <button
                          className="rc-meal-add-content-btn"
                          onClick={() => setMealContentEditorOpen(true)}
                          type="button"
                        >
                          <span className="rc-meal-add-content-icon">+</span>
                          <span className="rc-meal-add-content-text">
                            {t('calendar.suggested.addMealContent')}
                          </span>
                          <span className="rc-meal-add-content-arrow">›</span>
                        </button>
                      ) : (
                        <MealContentEditor
                          open={mealContentEditorOpen}
                          initialDraft={localMealDraft}
                          isSaving={isSavingMealContent}
                          onClose={() => setMealContentEditorOpen(false)}
                          onSave={handleSaveMealContent}
                          classNamePrefix="ai-suggested-event-modal"
                        />
                      ))}
                    {showV3RichContextSummary && !shouldShowMealEditorInRichContext && (
                      <RichContextSummary
                        sessionType="meal"
                        richContext={localRichContext}
                        onEditMealContent={
                          hasMealDetailsInRichContext && !!onSaveMealContent
                            ? () => setMealContentEditorOpen(true)
                            : undefined
                        }
                        enableMealSuggestionAccordion
                      />
                    )}
                    {showV3RichContextSummary && shouldShowMealEditorInRichContext && (
                      <div className="rc-summary">
                        <div className="rc-content-card">
                          <div className="rc-content-header">
                            <span className="rc-content-icon">🍽</span>
                            <span className="rc-content-type">
                              {t('calendar.suggested.pastMealDetails')}
                            </span>
                          </div>
                          <MealContentEditor
                            open={mealContentEditorOpen}
                            initialDraft={localMealDraft}
                            isSaving={isSavingMealContent}
                            onClose={() => setMealContentEditorOpen(false)}
                            onSave={handleSaveMealContent}
                            classNamePrefix="ai-suggested-event-modal"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Rich context summary (v3) — sleep & workout */}
                {showV3RichContextSummary &&
                  sessionType !== 'meal' &&
                  !shouldShowMealEditorInRichContext && (
                    <div className="ai-suggested-event-modal__section">
                      <RichContextSummary
                        sessionType={sessionType as 'sleep' | 'workout'}
                        richContext={localRichContext}
                      />
                    </div>
                  )}

                {/* Ask AI section */}
                {isPredicted && !disableInteraction && (
                  <Button
                    className="ai-suggested-event-modal__ask-ai-button"
                    onClick={onAskAI}
                    fullWidth
                    variant="outlined"
                  >
                    <SparkleIcon width={18} height={18} />
                    <Typography className="ai-suggested-event-modal__ask-ai-button-text">
                      {t('calendar.suggested.askAi')}
                    </Typography>
                  </Button>
                )}
                {canSelectEnergyFeedback && (
                  <div className="ai-suggested-event-modal__section">
                    <EventEnergyFeedback
                      selectedFeedback={selectedEnergyFeedback}
                      onSelectFeedback={handleEnergyFeedbackChange}
                      isDisabled={disableInteraction}
                      className="ai-suggested-event-modal__energy-feedback"
                    />
                  </div>
                )}

                {calendarDisplay && (
                  <div className="ai-suggested-event-modal__section">
                    <div className="ai-suggested-event-modal__calendar-chip">
                      <div
                        className="ai-suggested-event-modal__calendar-dot"
                        style={{ backgroundColor: calendarDisplay.color }}
                      />
                      <Typography className="ai-suggested-event-modal__calendar-text">
                        {calendarDisplay.label}
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>

          {/* Action buttons */}
          <div className="ai-suggested-event-modal__button-container">
            {!!discardText && !disableInteraction && (
              <IconButton
                onClick={() => {
                  if (shouldUseScopedRoutineDelete) {
                    runDeleteSafely();
                    return;
                  }

                  setDeleteConfirmOpen(true);
                }}
                className="ai-suggested-event-modal__discard-button"
                disabled={isSaving}
                size="small"
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            )}
            {(canUpdate || canAcceptCalendar) && (
              <>
                {canAcceptCalendar && (
                  <LoadingButton
                    className="ai-suggested-event-modal__cta-btn"
                    onClick={() =>
                      onAcceptCalendar(selectedCalendarId, editableStartTime, editableEndTime)
                    }
                    disabled={!canSaveCta}
                    loading={isSaving}
                    variant="contained"
                    fullWidth
                  >
                    <Typography className="ai-suggested-event-modal__cta-btn-text">
                      {t('calendar.suggested.acceptToCalendar')}
                    </Typography>
                  </LoadingButton>
                )}
                {canUpdate && (
                  <>
                    {(isTaskType || isRoutineType) && (
                      <LoadingButton
                        className={clsx(
                          'ai-suggested-event-modal__cta-btn',
                          'ai-suggested-event-modal__cta-btn--task-checkbox'
                        )}
                        onClick={() => {
                          if (isTaskType) {
                            handleTaskToggleComplete(!localTaskCompleted);
                          } else if (isRoutineType) {
                            handleRoutineToggleComplete(!localRoutineCompleted);
                          }
                        }}
                        disabled={
                          disableInteraction ||
                          (isTaskType && isTaskStatusUpdating) ||
                          (isRoutineType && isRoutineUpdating)
                        }
                        loading={
                          (isTaskType && isTaskStatusUpdating) ||
                          (isRoutineType && isRoutineUpdating)
                        }
                        variant="text"
                        fullWidth={false}
                      >
                        <TaskCheckBox
                          isCompleted={isTaskType ? localTaskCompleted : localRoutineCompleted}
                          onChange={() => {}}
                          size={14}
                          svgRatio={1}
                        />
                        <Typography className="ai-suggested-event-modal__cta-btn-text">
                          {isTaskType
                            ? localTaskCompleted
                              ? t('calendar.suggested.markIncomplete')
                              : t('calendar.suggested.markCompleted')
                            : localRoutineCompleted
                              ? t('calendar.suggested.markIncomplete')
                              : t('calendar.suggested.markCompleted')}
                        </Typography>
                      </LoadingButton>
                    )}
                    <LoadingButton
                      className="ai-suggested-event-modal__cta-btn"
                      onClick={() =>
                        !isSaving && onUpdate(editableStartTime, editableEndTime, editableTitle)
                      }
                      disabled={!canSaveCta}
                      loading={isSaving}
                      variant="contained"
                      fullWidth={false}
                    >
                      <Typography className="ai-suggested-event-modal__cta-btn-text">
                        {t('calendar.common.save')}
                      </Typography>
                    </LoadingButton>
                  </>
                )}
              </>
            )}
          </div>
        </Dialog>
        <BasicDialog
          visible={deleteConfirmOpen}
          title={t('calendar.suggested.confirmActionTitle', { action: discardText })}
          description={t('calendar.suggested.confirmActionDescription', {
            action: discardActionInSentence,
          })}
          ctaString={discardText}
          onYes={handleDiscard}
          onClose={() => setDeleteConfirmOpen(false)}
        />
      </>
    );
  }
);

export default AISuggestedEventModal;
